import { getUnlockCode } from "./useManifest";

// "Get it printed" → Anvil's checkout contract (gated): POST {API}/a/{shareToken}
// /checkout { code } → 200 { checkoutUrl } → redirect to Stripe hosted Checkout.
// The album's shareToken is manifest.id (opaque). The unlock `code` is required
// (defense-in-depth; server is stateless). Errors: 401/404/410/409/400.
const API_BASE = import.meta.env?.VITE_API_BASE as string | undefined;

export type CheckoutError =
  | "no_api"
  | "not_found"
  | "not_shared"
  | "not_buyable"
  | "failed";

/** Mint a Checkout Session and redirect to Stripe. Throws a CheckoutError code
 *  string on failure so the caller can surface a friendly message. */
export async function startCheckout(shareToken: string): Promise<void> {
  if (!API_BASE) throw "no_api" as CheckoutError;
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/a/${encodeURIComponent(shareToken)}/checkout`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ code: getUnlockCode() ?? "" }),
    });
  } catch {
    throw "failed" as CheckoutError;
  }
  if (res.status === 401) throw "failed" as CheckoutError; // stale/missing unlock
  if (res.status === 404) throw "not_found" as CheckoutError;
  if (res.status === 410) throw "not_shared" as CheckoutError;
  if (res.status === 409) throw "not_buyable" as CheckoutError;
  if (!res.ok) throw "failed" as CheckoutError;
  const data = (await res.json()) as { checkoutUrl?: string };
  if (!data.checkoutUrl) throw "failed" as CheckoutError;
  window.location.href = data.checkoutUrl; // → Stripe hosted Checkout
}
